import { QuestionnaireResponsesType, QuestionnaireType } from "@/common/types";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import {
  downloadQuestionnaireCsv,
  getQuestionnaireDetail,
} from "@/redux/actions/questionnaireAction";
import { RootState } from "@/redux/reducers";
import { convertDateToString } from "@/utils/date.util";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import QuestionnaireResponse from "./questionnaire-response";
import { isUserAdmin, isUserDoctor } from "@/utils/common.util";
import { Button } from "@/components/ui/button";

export const QuestionnaireDetail = () => {
  const [questionnaire, setQuestionnaire] = useState<QuestionnaireType>(
    {} as QuestionnaireType
  );
  const [isResponseOpen, setIsResponseOpen] = useState(false);

  const { questionnaireDetail } = useSelector(
    (state: RootState) => state.questionnaire
  );

  const params = useParams();
  const dispatch: any = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (questionnaireDetail) {
      setQuestionnaire(questionnaireDetail as QuestionnaireType);
    }
  }, [questionnaireDetail]);

  useEffect(() => {
    if (params.id !== undefined) {
      dispatch(getQuestionnaireDetail(Number(params.id)));
    }
  }, [params.id]);

  const handleQuestionnaireExport = (patientId: number | undefined) => {
    if (!patientId) {
      return;
    }
    dispatch(downloadQuestionnaireCsv(Number(patientId)));
  };

  const renderResponses = () => {
    return (
      <Collapsible
        open={isResponseOpen}
        onOpenChange={setIsResponseOpen}
        className="pt-4"
      >
        <CollapsibleTrigger asChild>
          <div className="flex items-center justify-between bg-secondary w-full px-4 py-2 rounded-lg">
            <div className="text-black font-semibold text-base ">
              {isResponseOpen ? "Collapse" : "Expand"} Responses
            </div>
            <div>{isResponseOpen ? <ChevronUp /> : <ChevronDown />}</div>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="">
          {questionnaire &&
            questionnaire.responses &&
            questionnaire.responses.map((responseItem, index) => (
              <QuestionnaireResponse
                key={index}
                data={responseItem as QuestionnaireResponsesType}
              />
            ))}
        </CollapsibleContent>
      </Collapsible>
    );
  };

  return (
    <>
      {questionnaire && (
        <div className="p-4 md:p-8">
          <div className="pb-4 flex items-center justify-end">
            {(isUserAdmin() || isUserDoctor()) && (
              <Button
                variant="secondary"
                onClick={() =>
                  handleQuestionnaireExport(questionnaire?.patient?.id)
                }
              >
                Download Questionnaire History
              </Button>
            )}
          </div>

          <div className="pb-6 flex flex-col md:flex-row items-start md:items-center justify-between h-auto md:h-[56px] gap-2">
            <div
              className="bg-secondary py-2 px-4 rounded-full font-bold"
              onClick={() => {
                navigate(`/test/${questionnaire.test_kit}`);
              }}
            >{`Test Kit: ${questionnaire.test_kit_label}`}</div>
            <div>
              Submitted on {convertDateToString(questionnaire.submission_time)}
            </div>
          </div>
          <div className="font-bold text-2xl pb-6">Questionnaire Response</div>
          <div className="flex flex-col md:flex-row gap-4 justify-between pb-6">
            <div>
              <p className="font-bold text-lg">
                Patient: {questionnaire.patient?.name}
              </p>
              <p className="text-gray-500 text-sm">
                {questionnaire.patient?.email}
              </p>
            </div>
            <div className="flex flex-col items-start md:items-end">
              <p className="font-bold text-lg">
                Doctor: {questionnaire.doctor?.name}
              </p>
              <p className="text-gray-500 text-sm">
                {questionnaire.doctor?.email}
              </p>
            </div>
          </div>

          <Separator />
          <div>{questionnaire && renderResponses()}</div>
        </div>
      )}
    </>
  );
};

export default QuestionnaireDetail;
